import Tool, { type ToolArgs } from '../../../tools/Tool';
import React, { useState, useRef } from 'react';
import { Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import styles from './EditLinkCard.module.css';
import toolbarStyles from '../../../components/Toolbar/Toolbar.module.css';
import classNames from 'classnames';
import { isLinkCardElement } from '../../../types/guards';
import useFocusElementToolbarInput from '../../../components/ElementToolbar/useFocusElementToolbarInput';
import type { Placement } from '@floating-ui/dom';

const EditLinkCardTextModal: React.FC<ToolArgs> = ({
  editor,
  element,
  toolbarHideModal,
}) => {
  const defaultTitle =
    element && isLinkCardElement(element) ? element.linkCard_title ?? '' : '';
  const [originalTitle] = useState(defaultTitle);
  const [title, setTitle] = useState(defaultTitle);

  const defaultDescription =
    element && isLinkCardElement(element)
      ? element.linkCard_description ?? ''
      : '';
  const [originalDescription] = useState(defaultDescription);
  const [description, setDescription] = useState(defaultDescription);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);

  useFocusElementToolbarInput(titleInputRef);

  if (!element) return null;

  const updateLinkCardProperties = (
    newTitle: string,
    newDescription: string
  ) => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(
      editor,
      {
        linkCard_title: newTitle,
        linkCard_description: newDescription,
      },
      {
        at: path,
      }
    );
  };

  const handleTitleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkCardProperties(title, description);
        ReactEditor.focus(editor);
        break;
      case 'Escape':
        toolbarHideModal?.();
        event.preventDefault();
        ReactEditor.focus(editor);
        break;
    }
  };

  const handleDescriptionKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        // Allow Enter to create new lines in textarea, but handle Ctrl+Enter or Cmd+Enter to submit
        if (event.ctrlKey || event.metaKey) {
          toolbarHideModal?.();
          event.preventDefault();
          updateLinkCardProperties(title, description);
          ReactEditor.focus(editor);
        }
        break;
      case 'Escape':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkCardProperties(originalTitle, originalDescription);
        ReactEditor.focus(editor);
        break;
    }
  };

  const handleTitleBlur = () => {
    if (title !== originalTitle || description !== originalDescription) {
      updateLinkCardProperties(title, description);
    }
  };

  const handleDescriptionBlur = () => {
    if (title !== originalTitle || description !== originalDescription) {
      updateLinkCardProperties(title, description);
    }
  };

  return (
    <div className={styles.editLink}>
      <label className={styles.label}>
        <div className={styles.labelName}>Title</div>
        <input
          ref={titleInputRef}
          type="text"
          className={classNames(
            'unstyled-input',
            toolbarStyles.toolbarInput,
            styles.input
          )}
          placeholder="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={handleTitleKeyDown}
          onBlur={handleTitleBlur}
        />
      </label>

      <label className={styles.label}>
        <div className={styles.labelName}>Description</div>
        <textarea
          ref={descriptionInputRef}
          className={classNames(
            'unstyled-input',
            toolbarStyles.toolbarInput,
            styles.input,
            styles.inputTextarea
          )}
          placeholder="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          onKeyDown={handleDescriptionKeyDown}
          onBlur={handleDescriptionBlur}
          rows={3}
        />
      </label>
    </div>
  );
};

// Edit the link card title and description.
class ToolEditLinkCardText extends Tool {
  static toolName = 'Edit link card text';
  static iconClass = 'fa-regular fa-edit';
  static withoutRefocus = true;

  // Show the modal view to edit the title and description.
  static action({ editor, element, toolbarShowModal }: ToolArgs): void {
    toolbarShowModal?.(ToolEditLinkCardText);
  }

  static renderToolbarButtonIcon({ editor, element }: ToolArgs): JSX.Element {
    return <span>Edit text</span>;
  }

  static renderToolbarModal(toolArgs: ToolArgs): JSX.Element {
    return <EditLinkCardTextModal {...toolArgs} />;
  }

  // Open the edit link card text modal when the toolbar opens if the title is empty.
  static openToolbarModal({ editor, element }: ToolArgs): boolean {
    if (!element || !isLinkCardElement(element)) return false;

    return !element.linkCard_title;
  }

  static tooltipPlacement(): Placement {
    return 'bottom';
  }
}

export default ToolEditLinkCardText;
